import React, { useState, useContext } from 'react'
import dayjs from 'dayjs'

import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { DatePicker, Space, Switch } from 'antd'

import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'
import { AbilityContext } from 'contexts/abilityContext'

import { CORRECT_FORMALITY_QUESTION_MUTATION } from 'graphql/LegalFormalities'
import FieldOptionsSelect from '../FieldOptionsSelect'
import FieldMulti from '../FieldMulti'

import {
  formatedResponse,
  validateResponse,
  clearResponse,
} from '../FormHelpers'

import {
  QuestionCorrection as Correction,
  QuestionResponse as Response,
  QuestionTitle as Title,
  QuestionWrapper as Wrapper,
} from '../styles'

interface IProps {
  nameKeys: string[]
  response: any
  correction?: any
}

const Question = (props: IProps) => {
  const { t } = useTranslation('legalFormalities')
  const { legalFormality } = useContext(CurrentLegalFormalityContext)
  const ability: any = useContext(AbilityContext)
  const formalistOnly = ability.can('update', 'LegalFormality')
  const [saveCorrection] = useMutation(CORRECT_FORMALITY_QUESTION_MUTATION)
  const { nameKeys, response, correction } = props
  const { type, question } = response
  const answer = response && (response.answers || response.answer)
  const correctedAnswer =
    correction && (correction.answers || correction.answer)

  const [editMode, setEditMode] = useState<boolean>(false)
  const [correctionValue, setCorrectionValue] = useState<any>(
    correctedAnswer || answer || ''
  )

  const editableType = type !== 'fileupload'

  const activateEditMode = () => {
    setEditMode(true)
  }
  const removeEditMode = () => {
    setEditMode(false)
  }

  // save response without correcting it
  const handleValidate = () => {
    validateResponse(legalFormality, saveCorrection, nameKeys, answer).then(
      () => setEditMode(false)
    )
  }

  // save corrected response
  const handleSave = () => {
    validateResponse(
      legalFormality,
      saveCorrection,
      nameKeys,
      correctionValue
    ).then(() => setEditMode(false))
  }

  const handleClear = () => {
    clearResponse(legalFormality, saveCorrection, nameKeys).then(() =>
      setEditMode(false)
    )
  }

  const handleCopy = () => {
    setCorrectionValue(answer)
  }

  const typedInput = () => {
    switch (type) {
      case 'boolean':
        return (
          <>
            <Switch
              defaultChecked={correctionValue === 'true'}
              onChange={checked => setCorrectionValue(checked.toString())}
            />
          </>
        )
      case 'datetime':
        return (
          <>
            <Space direction="vertical" size={12}>
              <DatePicker
                defaultValue={
                  !correctionValue ? undefined : dayjs(correctionValue)
                }
                format={'DD/MM/YYYY'}
                onChange={date =>
                  date &&
                  setCorrectionValue(date.format('YYYY-MM-DD').toString())
                }
              />
            </Space>
          </>
        )
      case 'radio':
      case 'dropdown':
      case 'checkbox':
        return (
          <FieldOptionsSelect
            nameKeys={nameKeys}
            defaultValue={correctionValue}
            checkboxes={type === 'checkbox'}
            setCorrection={setCorrectionValue}
          />
        )
      case 'fullname':
      case 'address':
        return (
          <FieldMulti
            type={type}
            defaultValue={correctionValue}
            setCorrection={setCorrectionValue}
          />
        )
      default:
        return (
          <>
            <input
              value={correctionValue}
              onChange={e => setCorrectionValue(e.target.value)}
            />
          </>
        )
    }
  }

  return (
    <Wrapper>
      <Title>
        <div className="header">
          <div>
            {question} ({nameKeys.join('.')})
          </div>
          {formalistOnly && !editMode && editableType && (
            <div>
              <a onClick={activateEditMode}>{t('form.btns.edit')}</a>
              {!correction && (
                <a onClick={handleValidate}>{t('form.btns.validate')}</a>
              )}
            </div>
          )}
        </div>
      </Title>

      <Response>
        {formatedResponse(t, nameKeys, type, answer, legalFormality)}
      </Response>

      {!editMode && correctedAnswer && (
        <Correction>
          {formatedResponse(t, nameKeys, type, correctedAnswer, legalFormality)}
        </Correction>
      )}
      {formalistOnly && editMode && editableType && (
        <>
          {typedInput()}
          <button onClick={handleClear}>{t('form.btns.clear')}</button>
          <button onClick={handleCopy}>{t('form.btns.copy')}</button>
          <button onClick={handleSave}>{t('form.btns.save')}</button>
          <button onClick={removeEditMode}>{t('form.btns.cancel')}</button>
        </>
      )}
    </Wrapper>
  )
}

export default Question
